import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Activity, MonitorDot, MessageSquareWarning, MessagesSquare, Loader2, Send, Wifi } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "belum pernah";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s lalu`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}j lalu`;
  return `${Math.floor(h / 24)}h lalu`;
}

function StatCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="bg-[#111827] border border-[#1F2937] rounded-xl p-5 flex items-center gap-4"
    >
      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${color}1a` }}>
        <Icon size={22} style={{ color }} />
      </div>
      <div>
        <div className="text-2xl font-display font-extrabold text-white leading-none">{value}</div>
        <div className="text-xs text-[#9CA3AF] mt-1">{label}</div>
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { isMaster } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({});

  const load = () =>
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => toast.error("Gagal memuat statistik")).finally(() => setLoading(false));

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const sendNotice = async (id) => {
    const msg = (notice[id] || "").trim();
    if (!msg) return;
    try {
      await api.post(`/devices/${id}/notice`, { message: msg });
      toast.success("Notice terkirim ke PC");
      setNotice({ ...notice, [id]: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onlineCount = stats.filter((s) => s.online).length;
  const badwordsTotal = stats.reduce((a, s) => a + s.badwords_today, 0);
  const chatTotal = stats.reduce((a, s) => a + s.chat_today, 0);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2">
          <Activity className="text-[#3B82F6]" size={30} /> Dashboard
        </h1>
        <p className="text-[#9CA3AF] mt-1 text-sm">Statistik aktivitas & status monitoring per PC hari ini.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={MonitorDot} label="Total PC" value={stats.length} color="#3B82F6" delay={0} />
        <StatCard icon={Wifi} label="PC Online" value={onlineCount} color="#10B981" delay={0.05} />
        <StatCard icon={MessagesSquare} label="Ketikan Hari Ini" value={chatTotal} color="#06B6D4" delay={0.1} />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#3B82F6]" size={28} /></div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-[#1F2937]">
            <h2 className="font-display text-lg font-semibold flex items-center gap-2">🔥 Statistik Aktivitas per PC</h2>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1F2937] hover:bg-transparent">
                  <TableHead className="text-[#9CA3AF]">No</TableHead>
                  <TableHead className="text-[#9CA3AF]">Nama PC</TableHead>
                  <TableHead className="text-[#9CA3AF]">Status</TableHead>
                  <TableHead className="text-[#9CA3AF]">💬 Ketikan</TableHead>
                  <TableHead className="text-[#9CA3AF]">🔁 Ping Terakhir</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.map((s, i) => (
                  <TableRow key={s.id} data-testid={`stat-row-${s.id}`} className="border-[#1F2937] hover:bg-[#0B0F17]/50">
                    <TableCell className="text-[#6B7280]">{i + 1}</TableCell>
                    <TableCell>
                      <button onClick={() => navigate(`/logs?device=${s.id}`)} className="font-semibold text-white hover:text-[#60A5FA] transition-colors" data-testid={`stat-open-${s.id}`}>{s.name}</button>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${s.online ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#EF4444]/10 text-[#EF4444]"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${s.online ? "bg-[#10B981]" : "bg-[#EF4444]"}`} />
                        {s.online ? "ONLINE" : "OFFLINE"}
                      </span>
                    </TableCell>
                    <TableCell><Badge className="bg-[#06B6D4]/15 text-[#22D3EE] border-[#06B6D4]/30">{s.chat_today}</Badge></TableCell>
                    <TableCell className="text-[#9CA3AF] text-sm">{timeAgo(s.last_ping)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </motion.div>
      )}
    </div>
  );
}
